import type { ReactNode } from 'react';
import { cn } from '@mlm/ui';

/** One line above the total: a label and a figure, optionally toned. */
export type TotalsRow = {
  label: ReactNode;
  value: ReactNode;
  tone?: 'default' | 'discount' | 'muted';
};

/**
 * The summary box of the cart panel and the checkout page: optional detail
 * rows, then the total in the accent colour. When `struck` is given it is the
 * figure before discounts, shown crossed out beside the total.
 */
export function TotalsBox({
  rows = [],
  totalLabel = 'Total',
  total,
  struck,
  className,
}: {
  rows?: TotalsRow[];
  totalLabel?: string;
  total: ReactNode;
  struck?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-[10px] bg-muted/60 p-4', className)}>
      {rows.length > 0 && (
        <div className="mb-3 space-y-1.5 border-b pb-3 text-base">
          {rows.map((r, i) => (
            <div
              key={i}
              className={cn(
                'flex justify-between gap-3',
                r.tone === 'discount' && 'text-success',
                r.tone === 'muted' && 'text-muted-foreground',
              )}
            >
              <span>{r.label}</span>
              <span className="tabular-nums">{r.value}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-lg font-semibold">{totalLabel}</span>
        <span className="flex items-baseline gap-2">
          {struck && (
            <span className="text-base tabular-nums text-muted-foreground line-through">
              {struck}
            </span>
          )}
          <span className="text-2xl font-semibold tabular-nums text-primary">{total}</span>
        </span>
      </div>
    </div>
  );
}
